// M43 — Changelog data layer (public reads).
//
// The /changelog page and the M42 broadcast composer both need to read
// changelog entries the alpha cohort sees. The admin CRUD surface lives
// in ./admin so the public page doesn't pull the admin module just to
// render a list.
//
// RLS filters drafts for non-admin sessions; everything here is shaped
// for the published view (`is_published = true`).

import { supabase } from './supabase';

/** One row from public.changelog_entries, camelCased for the React app. */
export interface ChangelogEntry {
  id: string;
  version: string;
  /** YYYY-MM-DD; null when the entry is a draft that hasn't shipped. */
  releasedAt: string | null;
  bodyMarkdown: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Map a DB row to the camelCased shape used in the app. Kept separate
 *  so the column list and the field mapping live in one place. */
function fromRow(row: Record<string, unknown>): ChangelogEntry {
  return {
    id: row.id as string,
    version: row.version as string,
    releasedAt: (row.released_at as string | null) ?? null,
    bodyMarkdown: row.body_markdown as string,
    isPublished: row.is_published as boolean,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

const PUBLIC_COLUMNS =
  'id,version,released_at,body_markdown,is_published,created_at,updated_at';

/** Every published changelog entry, newest version first. Sorts on the
 *  stored generated column `version_sort` so "0.1.18" comes before
 *  "0.1.9" (lexical sort gets that backwards). */
export async function listPublishedChangelog(): Promise<ChangelogEntry[]> {
  const { data, error } = await supabase
    .from('changelog_entries')
    .select(PUBLIC_COLUMNS)
    .eq('is_published', true)
    .order('version_sort', { ascending: false });
  if (error) throw new Error(`Failed to load changelog: ${error.message}`);
  return (data ?? []).map((r) => fromRow(r as Record<string, unknown>));
}

/** The most-recently-released published entry. Used by the M42
 *  Announcements page to pre-fill the "What's new" email template.
 *  Returns null if the table has no published rows (test envs /
 *  fresh installs) so callers can fall back cleanly. */
export async function getLatestChangelog(): Promise<ChangelogEntry | null> {
  const { data, error } = await supabase
    .from('changelog_entries')
    .select(PUBLIC_COLUMNS)
    .eq('is_published', true)
    .order('version_sort', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(`Failed to load latest changelog: ${error.message}`);
  return data ? fromRow(data as Record<string, unknown>) : null;
}
